import React, { useRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OurSolution.css";

import arrowLeft from "../../../../assets/icons/left.svg";
import arrowRight from "../../../../assets/icons/right.svg";
import { FiArrowUpRight } from "react-icons/fi";
import {
  fallbackOurSolution,
  getOurSolutionDisplayCards,
  hasOurSolutionSectionData,
} from "../../../../utils/ourSolutionCards";

const OurSolution = ({ content }) => {
  const { i18n } = useTranslation();
  const sliderRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [ourSolutionContent, setOurSolutionContent] = useState(fallbackOurSolution);
  const isRtl = i18n.dir(i18n.language) === "rtl";

  useEffect(() => {
    setOurSolutionContent(hasOurSolutionSectionData(content) ? content : fallbackOurSolution);
  }, [content]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current || !sliderRef.current) return;
      e.preventDefault();
      const el = sliderRef.current;
      el.scrollLeft = dragScrollLeft.current - (e.clientX - dragStartX.current);
    };

    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const el = sliderRef.current;
      if (el) {
        el.style.cursor = "";
        el.style.userSelect = "";
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onSliderMouseDown = (e) => {
    const el = sliderRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
    e.preventDefault();
  };

  const solutions = useMemo(
    () => getOurSolutionDisplayCards(ourSolutionContent),
    [ourSolutionContent]
  );

  const scroll = (direction) => {
    const container = sliderRef.current;
    const scrollAmount = 360;
    const logicalDirection = isRtl
      ? direction === "left"
        ? "right"
        : "left"
      : direction;

    container.scrollBy({
      left: logicalDirection === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className="solutions">
      <div className="solutions-container">

        <div className="solutions-left">
          <span className="tag">{ourSolutionContent.tag}</span>

          <h2>
            {ourSolutionContent.titleLine1} <br />
            {ourSolutionContent.titleLine2} <br />
            <span>{ourSolutionContent.titleLine3}</span>
          </h2>

          <p>
            {ourSolutionContent.description}
          </p>

          <div className="arrows">
            <button
              type="button"
              onClick={() => scroll("left")}
              className="arrowcontainer"
              aria-label="Scroll solutions left"
            >
              <img src={isRtl ? arrowRight : arrowLeft} alt="" draggable={false} />
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              className="arrowcontainer"
              aria-label="Scroll solutions right"
            >
              <img src={isRtl ? arrowLeft : arrowRight} alt="" draggable={false} />
            </button>
          </div>
        </div>

        <div className="solutions-right">
          <div
            className="slider"
            ref={sliderRef}
            onMouseDown={onSliderMouseDown}
          >

            {solutions.map((item, index) => (
              <Link className="card" key={item.key || index} to={item.link}>

                <div className="card-top">
                  <div className="card-icon">
                    <img src={item.icon} alt={item.title} draggable={false} />
                  </div>
                </div>

                <div className="card-bottom">
                  <h3>{item.title}</h3>

                  <p>{item.description}</p>

                  <div className="more">
                    {ourSolutionContent.moreDetailsText}{" "}
                    <FiArrowUpRight />
                  </div>
                </div>

              </Link>
            ))}

          </div>
        </div>

      </div>
    </section>
  );
};

export default OurSolution;
